import { useMachine } from '@xstate/react'
import { PageId } from 'constants/pages'
import { XSTATE_CONFIGS_MAP } from 'constants/xstate-configs-map'
import type { IFlowRouterContext } from 'contexts/flowRouterContext'
import { getCohortSteps } from 'helpers/getCohortSteps'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createMachine } from 'xstate'
import { useShallow } from 'zustand/shallow'
import { useZustandStore } from 'zustandGlobalStore'

export const useCreateXStateMachine = (): {
  flowRouterContext: IFlowRouterContext
  isPageLoaded: boolean
} => {
  const { cohort, currentPageId } = useZustandStore(
    useShallow((state) => ({
      cohort: state.lead?.cohort || state.initialCohort,
      currentPageId: state.currentPageId,
    })),
  )
  const [isPageLoaded, setPageLoaded] = useState(false)

  // Resolved once per cohort. For intl_2lw this must run after the
  // abtest_web_127_first_screen variant is known (SubscribeFlow gates
  // FlowRouter's mount on it), so the snapshot captured here for the
  // machine's lazy initializer below is already correct.
  const cohortSteps = useMemo(() => getCohortSteps(cohort), [cohort])

  useEffect(() => {
    setPageLoaded(false)
  }, [currentPageId])

  const [current, send] = useMachine(() =>
    createMachine({
      id: 'flowRouter',
      initial: 'initial',
      states: XSTATE_CONFIGS_MAP[cohort](cohortSteps),
      predictableActionArguments: true,
    }),
  )

  useEffect(() => {
    const pageIdFromStateMachine = current.value as unknown as PageId
    const pageIndexFromStateMachine = cohortSteps.findIndex(
      (page) => page.pageId === (current.value as unknown as PageId),
    )

    useZustandStore.setState({
      currentPageId: pageIdFromStateMachine,
      currentPageIndex: pageIndexFromStateMachine,
      currentPageNumber:
        pageIndexFromStateMachine >= 0 ? pageIndexFromStateMachine + 1 : 0,
    })
  }, [current.value, cohortSteps])

  const goToNextStep = useCallback(() => {
    setPageLoaded(false)
    send('NEXT_FLOW_STEP')
  }, [send])

  const goToPreviousStep = useCallback(() => {
    send('PREVIOUS_FLOW_STEP')
  }, [send])

  const goToSkipOneStepBack = useCallback(() => {
    send('SKIP_ONE_STEP_BACK')
  }, [send])

  const goToSkipOneStep = useCallback(() => {
    send('SKIP_ONE_STEP')
  }, [send])

  const goToPostPaymentContinue = useCallback(() => {
    setPageLoaded(false)
    send('POST_PAYMENT_CONTINUE')
  }, [send])

  return {
    flowRouterContext: {
      onNextStep: goToNextStep,
      onSkipOneStep: goToSkipOneStep,
      onPreviousStep: goToPreviousStep,
      onSkipOneStepBack: goToSkipOneStepBack,
      onPostPaymentContinue: goToPostPaymentContinue,
      onPageLoad: () => setPageLoaded(true),
    },
    isPageLoaded,
  }
}
